import { useEffect } from "react";
import styled from "styled-components";

import {
    VOTEBOX_FONT_SIZE_BODY,
    VOTEBOX_FONT_SIZE_SMALL,
    VOTEBOX_FONT_WEIGHT_SEMIBOLD,
    VOTEBOX_SECONDARY_TEXT_COLOR,
    VOTEBOX_SUCCESS_COLOR,
} from "../common/voteboxTokens";
import { useTranslation } from "react-i18next";

import { QuestionType } from "types/dataVoteType";
import useVoteState, { VoteState, Person, VoteStatus } from "./useVoteState";
import { useHolderVoteMustBeAppliedToProxies } from "../common/AutomaticVoteForProxiesComponents/useHolderVoteMustBeAppliedToProxies";

import VoteHeader from "../common/Header/VoteHeader";
import { getQuestionTitleId } from "../common/VoteGroup";
import VoteBasic from "../common/VoteBasic";
import VoteDistribution from "../common/VoteDistribution";
import ActionSection from "../common/ActionSection";
import getGlobalStatus from "./services/getGlobalStatus";
import { getRemainingWeight } from "../common/services/getRemainingWeight";
import { RemainingVotes } from "../common/types";
import ToggleSwitch from "../common/AutomaticVoteForProxiesComponents/ToggleSwitch";
import ProxiesAccordion from "../common/AutomaticVoteForProxiesComponents/ProxiesAccordion";
import ParticipantHeader from "../common/ParticipantHeader";
import emitAsyncAnswer from "components/helper/emitAsyncAnswer";
import NoVoteAllowedMessage from "../common/NoVoteAllowedMessage";
import { useSession } from "components/helper/sessionContext";

type Props = {
    question: QuestionType;
    user: Person;
    proxies: Array<Person>;
    publicKey: string;
};

const isZoomTemplate = document
    .getElementById("DesignVote")
    ?.classList.contains("streaming");

const Deliberation = ({ question, user, proxies, publicKey }: Props) => {
    const [votes, dispatchVoteAction] = useVoteState(question, proxies, user);
    const {
        holderVoteMustBeAppliedToProxies,
        setHolderVoteMustBeAppliedToProxies,
    } = useHolderVoteMustBeAppliedToProxies(
        question.authorizeWeightDistribution,
    );

    useEffect(() => {
        const userAnswer = votes[0].answers.find((answer) => answer.weight > 0);

        if (holderVoteMustBeAppliedToProxies && userAnswer) {
            dispatchVoteAction({
                type: "replaceVoteForEveryParticipant",
                id: userAnswer.id,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatchVoteAction, holderVoteMustBeAppliedToProxies]);

    const { t } = useTranslation();
    const { socket } = useSession();

    const participants = [user, ...proxies];
    const hasProxies = proxies.length > 0;
    // In case of proxies, the options of each voter are side by side.
    const proposalsOrientation = hasProxies ? "horizontal" : "vertical";
    const titleId = getQuestionTitleId(question.id);

    const handleValidate = async (voterId: string) => {
        const voteToSend = votes.find(
            (vote) => vote.voterId === voterId,
        ) as VoteState;

        const answersWithWeight = voteToSend.answers.filter(
            (answer) => answer.weight > 0,
        );

        const data = {
            votingDeviceId: voterId,
            publicKey: publicKey,
            answers: answersWithWeight.map((answer) => ({
                questionId: question.id,
                answer: answer.id,
                weight: answer.weight,
            })),
        };

        dispatchVoteAction({
            type: "sendAnswersRequest",
            voterId: voterId,
        });

        try {
            await emitAsyncAnswer(socket, data);
            dispatchVoteAction({
                type: "sendAnswersSuccess",
                voterId: voterId,
            });
        } catch {
            dispatchVoteAction({
                type: "sendAnswersError",
                voterId: voterId,
            });
        }
    };

    const handleValidateProxies = () => {
        votes.forEach((vote) => {
            if (vote.voteStatus === VoteStatus.PendingValidation) {
                handleValidate(vote.voterId);
            }
        });
    };

    const renderVoteBasic = (voter: Person, state: VoteState) => {
        return (
            <VoteBasic
                propositions={question.propositions}
                answers={state.answers}
                isSecret={question.isSecret}
                voteStatus={state.voteStatus}
                replaceProposal={(propositionId: string) => {
                    //Change all answers in the case of automatic vote for proxies toggle's is active with user's answer.
                    if (
                        voter.id === user.id &&
                        holderVoteMustBeAppliedToProxies
                    ) {
                        dispatchVoteAction({
                            type: "replaceVoteForEveryParticipant",
                            id: propositionId,
                        });
                    } else {
                        dispatchVoteAction({
                            type: "updateWeightVote",
                            id: propositionId,
                            voterId: voter.id,
                            weight: voter.weight,
                        });

                        setHolderVoteMustBeAppliedToProxies(false);
                    }
                }}
                proposalsOrientation={proposalsOrientation}
                voterName={voter.displayName}
                titleId={titleId}
                groupVoterName={hasProxies ? voter.displayName : undefined}
            />
        );
    };

    const renderVoteDistribution = (voter: Person, state: VoteState) => {
        return (
            <VoteDistribution
                propositions={question.propositions}
                answers={state.answers}
                isSecret={question.isSecret}
                voterWeight={voter.weight}
                voteStatus={state.voteStatus}
                updateWeight={(propositionId: string, weight: number) => {
                    dispatchVoteAction({
                        id: propositionId,
                        weight,
                        type: "updateWeightVote",
                        voterId: voter.id,
                    });
                }}
                proposalsOrientation={proposalsOrientation}
                titleId={titleId}
                groupVoterName={hasProxies ? voter.displayName : undefined}
            />
        );
    };

    const renderVoteDistributionOrBasic = (
        participant: Person,
        participantVote: VoteState,
    ) => {
        if (0 === participant.weight) {
            return <NoVoteAllowedMessage displayName={user.displayName} />;
        }

        if (question.authorizeWeightDistribution && 1 !== participant.weight) {
            // No proxies + weight distribution
            return renderVoteDistribution(participant, participantVote);
        }

        // No proxies + no weight distribution or plurinominal
        return renderVoteBasic(participant, participantVote);
    };

    const voteStatusArray = votes.map((vote) => vote.voteStatus);

    const renderSucceededMessageSend = (item: number) => {
        return voteStatusArray[item] === VoteStatus.Success ? (
            <StyledSuccessVotedMessage>
                {t(`sentSucceded.${window.env.REACT_APP_THEME}`)}
            </StyledSuccessVotedMessage>
        ) : null;
    };

    const renderWithAutomaticVoteForProxies = () => {
        return (
            <>
                {/* Holder voter part */}
                <>
                    {0 !== user.weight && (
                        <StyledNumberOfVoices>
                            {t("numberOfVote")} {user.weight}
                        </StyledNumberOfVoices>
                    )}
                    {renderSucceededMessageSend(0)}
                    {renderVoteDistributionOrBasic(user, votes[0])}
                    {0 !== user.weight && (
                        <ToggleSwitch
                            checked={holderVoteMustBeAppliedToProxies}
                            onChange={setHolderVoteMustBeAppliedToProxies}
                        />
                    )}
                </>
                {/* Proxies voters part inside the accordion (children props) */}
                <ProxiesAccordion>
                    <>
                        {proxies.map((proxy: Person, item: number) => {
                            const proxyVote = votes.find(
                                (vote) => vote.voterId === proxy.id,
                            ) as VoteState;

                            return (
                                <div key={proxy.id}>
                                    <NameWrapper>
                                        <StyledName>{t("proxy")}:</StyledName>
                                        <StyledName>
                                            {proxy.displayName}
                                        </StyledName>
                                    </NameWrapper>

                                    <>
                                        {proxy.weight > 1 ? (
                                            <StyledNumberOfVoices>
                                                {t("numberOfVote")}
                                                {proxy.weight}
                                            </StyledNumberOfVoices>
                                        ) : null}

                                        {renderSucceededMessageSend(item + 1)}

                                        {renderVoteDistributionOrBasic(
                                            proxy,
                                            proxyVote,
                                        )}
                                    </>
                                </div>
                            );
                        })}
                    </>
                </ProxiesAccordion>
            </>
        );
    };

    const renderVote = () => {
        if (hasProxies && !question.authorizeWeightDistribution) {
            return renderWithAutomaticVoteForProxies();
        }

        return participants.map((participant) => {
            const participantVote = votes.find(
                (vote) => vote.voterId === participant.id,
            ) as VoteState;
            const proxy = proxies.find((p) => p.id === participant.id);

            return (
                <div key={participant.id}>
                    {proxy && (
                        <NameWrapper>
                            <StyledName>{t("proxy")}:</StyledName>
                            <StyledName>{proxy.displayName}</StyledName>
                        </NameWrapper>
                    )}
                    <ParticipantHeader
                        participant={participant}
                        participantVote={participantVote}
                        voteIsWithDistribution={
                            question.authorizeWeightDistribution
                        }
                    />
                    {renderVoteDistributionOrBasic(
                        participant,
                        participantVote,
                    )}
                </div>
            );
        });
    };

    // Votes left to distribute by each voter whose vote is incomplete, named
    // in the message when the user also votes for proxies.
    function getRemainingVotesByVoter(): Array<RemainingVotes> {
        if (
            !question.authorizeWeightDistribution ||
            1 === participants.length
        ) {
            return [];
        }

        return participants.flatMap((participant) => {
            const participantVote = votes.find(
                (vote) => vote.voterId === participant.id,
            ) as VoteState;

            if (VoteStatus.Incomplete !== participantVote.voteStatus) {
                return [];
            }

            return [
                {
                    displayName: participant.displayName,
                    remainingWeight: getRemainingWeight(
                        participant.weight,
                        participantVote.answers,
                    ),
                },
            ];
        });
    }

    // Alone, the user reads "il vous reste N voix à répartir". Only an
    // incomplete vote is counted: a vote sent before the page was loaded comes
    // back without its answers, and would disable "Modifier le vote".
    function getUserRemainingWeight(userVote: VoteState): number {
        if (
            !question.authorizeWeightDistribution ||
            1 !== participants.length ||
            VoteStatus.Incomplete !== userVote.voteStatus
        ) {
            return 0;
        }

        return getRemainingWeight(user.weight, userVote.answers);
    }

    const renderActionSection = () => {
        const userVote = votes.find(
            (vote) => vote.voterId === user.id,
        ) as VoteState;

        return (
            <ActionSection
                remainingWeight={getUserRemainingWeight(userVote)}
                remainingVotesByVoter={getRemainingVotesByVoter()}
                voteStatus={
                    participants.length > 1
                        ? getGlobalStatus(voteStatusArray)
                        : userVote.voteStatus
                }
                validateVote={
                    participants.length > 1
                        ? handleValidateProxies
                        : () => {
                              handleValidate(user.id);
                          }
                }
                resetVote={() => {
                    dispatchVoteAction({
                        type: "resetVote",
                    });
                }}
                canAnswerBeUpdated={question.canAnswerBeUpdated}
            />
        );
    };

    return (
        <Wrapper>
            <WrapperVote>
                <VoteHeader question={question} />
                {renderVote()}
                {getGlobalStatus(voteStatusArray) === VoteStatus.NotAllowed
                    ? null
                    : renderActionSection()}
            </WrapperVote>
        </Wrapper>
    );
};

const Wrapper = styled.div`
    width: 100%;
`;

const WrapperVote = styled.div`
    width: ${() => (isZoomTemplate ? "90%" : "100%")};
    @media (max-width: 768px) {
        max-width: 350px;
    }
`;

const NameWrapper = styled.div`
    display: flex;
    font-size: ${VOTEBOX_FONT_SIZE_BODY};
`;

const StyledName = styled.p`
    margin-bottom: 0;
    font-weight: ${VOTEBOX_FONT_WEIGHT_SEMIBOLD};
    margin-right: 5px;
    }
`;

const StyledSuccessVotedMessage = styled.p`
    color: ${VOTEBOX_SUCCESS_COLOR};
    margin: 0;
    font-size: 0.85rem;
`;

const StyledNumberOfVoices = styled.div`
    color: ${VOTEBOX_SECONDARY_TEXT_COLOR};
    padding-bottom: 0.5rem;
    font-size: ${VOTEBOX_FONT_SIZE_SMALL};
`;

export default Deliberation;
